import { parseAddressComponents, toAddressQuery, type ParsedAddress } from "./address";

export interface Coordinates {
  latitude: number;
  longitude: number;
}

export interface AddressVerification {
  isVerified: boolean;
  suggestedAddress: ParsedAddress | null;
  /** Where the geocoder placed the entered address, when it returned a match. */
  coordinates?: Coordinates;
}

const UNVERIFIED: AddressVerification = {
  isVerified: false,
  suggestedAddress: null,
};

function postalCodeMatches(
  enteredPostalCode: string,
  returnedPostalCode: string,
  countryCode: string,
): boolean {
  const normalize = (postalCode: string) =>
    postalCode.trim().toUpperCase().replace(/\s+/g, "");
  const entered = normalize(enteredPostalCode);
  const returned = normalize(returnedPostalCode);

  if (!entered || !returned) return false;
  if (countryCode.toUpperCase() !== "US") return entered === returned;

  const usPostalCodePattern = /^\d{5}(?:-?\d{4})?$/;
  if (!usPostalCodePattern.test(entered) || !usPostalCodePattern.test(returned)) {
    return false;
  }

  const enteredDigits = entered.replace(/\D/g, "");
  const returnedDigits = returned.replace(/\D/g, "");
  return enteredDigits.length === 5
    ? returnedDigits.slice(0, 5) === enteredDigits
    : enteredDigits === returnedDigits;
}

function getReturnedPostalCode(
  components: google.maps.GeocoderAddressComponent[],
): string {
  const postalCode = components.find((component) =>
    component.types.includes("postal_code"),
  )?.short_name ?? "";
  const suffix = components.find((component) =>
    component.types.includes("postal_code_suffix"),
  )?.short_name;

  return suffix && !postalCode.includes(suffix)
    ? `${postalCode}-${suffix}`
    : postalCode;
}

/**
 * Geocodes an address to decide whether it needs user confirmation. An address
 * counts as verified only on an exact rooftop match.
 */
export async function verifyAddress(
  address: ParsedAddress,
): Promise<AddressVerification> {
  const query = toAddressQuery(address);

  if (!query || typeof google === "undefined" || !google.maps?.importLibrary) {
    return UNVERIFIED;
  }

  try {
    const { Geocoder } = (await google.maps.importLibrary(
      "geocoding",
    )) as google.maps.GeocodingLibrary;
    const { results } = await new Geocoder().geocode({ address: query });
    const [match] = results;

    if (!match) return UNVERIFIED;

    const components = match.address_components;
    if (!components) return UNVERIFIED;

    const suggestedAddress = {
      ...parseAddressComponents(components),
      addressLine2: address.addressLine2,
    };
    const hasRequiredSuggestionFields = Boolean(
      suggestedAddress.addressLine1.trim() &&
      suggestedAddress.city.trim() &&
      suggestedAddress.state.trim() &&
      suggestedAddress.zip.trim() &&
      suggestedAddress.countryCode.trim(),
    );

    const location = match.geometry?.location;

    return {
      isVerified:
        match.geometry?.location_type === "ROOFTOP" &&
        match.partial_match !== true &&
        hasRequiredSuggestionFields &&
        postalCodeMatches(
          address.zip,
          getReturnedPostalCode(components),
          address.countryCode,
        ),
      suggestedAddress: hasRequiredSuggestionFields ? suggestedAddress : null,
      ...(location && {
        coordinates: { latitude: location.lat(), longitude: location.lng() },
      }),
    };
  } catch {
    return UNVERIFIED;
  }
}
